import axios from 'axios'
import { smartLocalStorageClear } from '../utils/localStorageUtils'

class VersionService {
    constructor() {
        this.currentVersion = null
        this.lastCheckTime = null
        this.checkInterval = null
        this.checkFrequency = 15 * 60 * 1000 // 15 минут
        this.forceUpdate = false
        this.updateMessage = ''
        this.serverVersion = null
    }

    /**
     * Инициализация сервиса проверки версии
     */
    init() {
        this.currentVersion = this.getClientVersion()
        this.startPeriodicCheck()
        
        // Проверяем версию при загрузке страницы
        this.checkVersion()
    }

    /**
     * Получить версию клиента из package.json или localStorage
     */
    getClientVersion() {
        // Используем версию из глобальной переменной, установленной в checkVersionApp.js
        return window.CLIENT_VERSION || '0.9.96'
    }

    /**
     * Запустить периодическую проверку версии
     */
    startPeriodicCheck() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval)
        }

        this.checkInterval = setInterval(() => {
            this.checkVersion()
        }, this.checkFrequency)
    }

    /**
     * Остановить периодическую проверку
     */
    stopPeriodicCheck() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval)
            this.checkInterval = null
        }
    }

    /**
     * Проверить версию с сервера
     */
    async checkVersion() {
        try {
            const response = await axios.post('/api/version/check', {
                version: this.currentVersion
            })

            const { needs_update, force_update, current_version, update_message } = response.data

            this.forceUpdate = force_update
            this.updateMessage = update_message

            this.serverVersion = current_version

            if (needs_update) {
                this.handleUpdateAvailable(force_update, update_message)
            }

            this.lastCheckTime = new Date()
        } catch (error) {
            console.warn('Ошибка при проверке версии:', error)
            
            // Если обычная проверка не удалась, попробуем свежий endpoint
            if (error.response && error.response.status >= 500) {
                await this.checkVersionFresh()
            }
        }
    }

    /**
     * Проверить версию с принудительной очисткой кеша
     */
    async checkVersionFresh() {
        try {
            console.log('Попытка получить свежую версию с очисткой кеша...')
            
            const response = await axios.get('/api/version/fresh')
            const { version, force_update, update_message } = response.data

            this.forceUpdate = force_update
            this.updateMessage = update_message
            this.serverVersion = version

            // Сравниваем с текущей версией клиента
            const needsUpdate = this.compareVersions(this.currentVersion, version)
            
            if (needsUpdate) {
                this.handleUpdateAvailable(force_update, update_message)
            }

            this.lastCheckTime = new Date()
            console.log('Свежая версия получена:', version)
        } catch (error) {
            console.warn('Ошибка при получении свежей версии:', error)
        }
    }

    /**
     * Сравнить версии
     */
    compareVersions(version1, version2) {
        // Простое сравнение версий (можно улучшить)
        return version1 !== version2
    }

    /**
     * Обработка доступного обновления
     */
    handleUpdateAvailable(isForceUpdate, message) {
        // Уже перезагружались ради этой версии, а бандл всё ещё старый: повторная перезагрузка
        // не поможет (бандл на сервере не обновился или закеширован), не зацикливаем пользователя
        if (this.serverVersion && this.getReloadAttempt() === this.serverVersion) {
            console.warn(`Версия клиента ${this.currentVersion} не совпадает с серверной ${this.serverVersion} после перезагрузки`)
            return
        }

        // Эмитируем событие для компонентов
        if (window.App && window.App.$emit) {
            window.App.$emit('version-update-available', {
                forceUpdate: isForceUpdate,
                message: message,
                timestamp: new Date()
            })
        }

        // Если критическое обновление - показываем немедленно
        if (isForceUpdate) {
            this.showForceUpdateNotification(message)
        }
    }

    /**
     * Показать уведомление о критическом обновлении
     */
    showForceUpdateNotification(message) {
        // Используем SweetAlert2 для критических обновлений
        if (window.Swal) {
            window.Swal.fire({
                title: 'Критическое обновление',
                text: message,
                icon: 'warning',
                showCancelButton: false,
                confirmButtonText: 'Перезагрузить сейчас',
                allowOutsideClick: false,
                allowEscapeKey: false,
                timer: 30000, // Автоматическая перезагрузка через 30 секунд
                timerProgressBar: true,
                didOpen: () => {
                    // Показываем прогресс-бар
                    const timerInterval = setInterval(() => {
                        const content = window.Swal.getContent()
                        if (content) {
                            const timerLeft = window.Swal.getTimerLeft()
                            const timer = content.querySelector('.swal2-timer')
                            if (timer) {
                                timer.textContent = `Автоматическая перезагрузка через ${Math.ceil(timerLeft / 1000)} сек.`
                            }
                        }
                    }, 1000)
                    
                    window.Swal.getTimerLeft = () => {
                        clearInterval(timerInterval)
                        return 0
                    }
                }
            }).then(() => {
                this.reloadPage()
            })
        }
    }

    /**
     * Перезагрузить страницу
     */
    reloadPage() {
        // Очищаем localStorage если нужно, но сохраняем данные авторизации
        if (this.forceUpdate) {
            smartLocalStorageClear('критическое обновление приложения')
        }
        
        this.setReloadAttempt(this.serverVersion)

        // Принудительная перезагрузка без кеша
        window.location.reload(true)
    }

    /**
     * Версия, ради которой страница уже перезагружалась в этой вкладке
     */
    getReloadAttempt() {
        try {
            return sessionStorage.getItem('versionReloadAttempt')
        } catch (e) {
            return null
        }
    }

    setReloadAttempt(version) {
        try {
            if (version) {
                sessionStorage.setItem('versionReloadAttempt', version)
            }
        } catch (e) {
            // sessionStorage недоступен — просто перезагружаемся
        }
    }

    /**
     * Получить информацию о последней проверке
     */
    getLastCheckInfo() {
        return {
            lastCheckTime: this.lastCheckTime,
            currentVersion: this.currentVersion,
            forceUpdate: this.forceUpdate,
            updateMessage: this.updateMessage
        }
    }

    /**
     * Установить частоту проверки
     */
    setCheckFrequency(milliseconds) {
        this.checkFrequency = milliseconds
        this.startPeriodicCheck()
    }
}

// Создаем единственный экземпляр сервиса
const versionService = new VersionService()

export default versionService
